import React, { useState } from 'react';
import { ConsultarPrecoFipeResult, VersaoFipeNormalizada } from '../types';
import { 
  ArrowLeft, 
  DollarSign,
  TrendingUp,
  TrendingDown,
  Minus,
  Car,
  Calendar,
  Copy,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  BarChart3,
  Tag
} from 'lucide-react';

interface ConsultaPrecoFipeResultProps {
  data: ConsultarPrecoFipeResult;
  onBack: () => void;
}

const ConsultaPrecoFipeResult: React.FC<ConsultaPrecoFipeResultProps> = ({ data, onBack }) => {
  const [selectedVersionIndex, setSelectedVersionIndex] = useState(0);
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const selectedVersion = data.versoes[selectedVersionIndex];

  const getTendenciaIcon = (tendencia: 'alta' | 'baixa' | 'estavel') => {
    switch (tendencia) {
      case 'alta':
        return <TrendingUp className="w-5 h-5 text-green-400" />;
      case 'baixa':
        return <TrendingDown className="w-5 h-5 text-red-400" />;
      default:
        return <Minus className="w-5 h-5 text-slate-400" />;
    }
  };

  const getTendenciaColor = (tendencia: 'alta' | 'baixa' | 'estavel') => {
    switch (tendencia) {
      case 'alta':
        return 'text-green-400';
      case 'baixa':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  const getTendenciaBg = (tendencia: 'alta' | 'baixa' | 'estavel') => {
    switch (tendencia) {
      case 'alta':
        return 'bg-green-500/10 border-green-500/30';
      case 'baixa':
        return 'bg-red-500/10 border-red-500/30';
      default:
        return 'bg-slate-500/10 border-slate-500/30';
    }
  };

  // Calculate chart dimensions
  const chartWidth = 100; // percentage
  const chartHeight = 120;
  
  const getChartPath = (version: VersaoFipeNormalizada) => {
    if (version.historico.length === 0) return '';
    
    const prices = version.historico.map(h => h.preco);
    const minPrice = Math.min(...prices) * 0.98;
    const maxPrice = Math.max(...prices) * 1.02;
    const priceRange = maxPrice - minPrice;
    
    const points = version.historico.map((h, i) => {
      const x = (i / (version.historico.length - 1)) * chartWidth;
      const y = chartHeight - ((h.preco - minPrice) / priceRange) * chartHeight;
      return `${x},${y}`;
    });
    
    return `M ${points.join(' L ')}`;
  };

  const getChartArea = (version: VersaoFipeNormalizada) => {
    if (version.historico.length === 0) return '';
    
    const prices = version.historico.map(h => h.preco);
    const minPrice = Math.min(...prices) * 0.98;
    const maxPrice = Math.max(...prices) * 1.02;
    const priceRange = maxPrice - minPrice;
    
    const points = version.historico.map((h, i) => {
      const x = (i / (version.historico.length - 1)) * chartWidth;
      const y = chartHeight - ((h.preco - minPrice) / priceRange) * chartHeight;
      return `${x},${y}`;
    });
    
    return `M 0,${chartHeight} L ${points.join(' L ')} L ${chartWidth},${chartHeight} Z`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-green-950/10 to-slate-950">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Header Banner */}
          <div className="bg-gradient-to-r from-green-600 to-emerald-600 p-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                <DollarSign className="w-10 h-10 text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">Preço FIPE</h1>
                <p className="text-green-100 text-sm mt-1">
                  Tabela de referência de preços do mercado
                </p>
              </div>
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-800/50 rounded-xl">
                <Car className="w-6 h-6 text-slate-400" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p className="text-2xl font-mono font-bold text-white tracking-wider">{data.placa}</p>
                  <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-xs font-medium rounded-full">
                    {data.versoes.length} versão(ões)
                  </span>
                </div>
                <p className="text-slate-400">
                  {data.dadosVeiculo.marca} {data.dadosVeiculo.modelo} • {data.dadosVeiculo.anoFabricacao}/{data.dadosVeiculo.anoModelo}
                </p>
              </div>
              <div className="text-right">
                <p className="text-xs text-slate-500">Consultado em</p>
                <p className="text-sm text-slate-400">{data.dataConsulta}</p>
              </div>
            </div>
          </div>

          {/* Version Selector */}
          {data.versoes.length > 1 && (
            <div className="p-6 border-b border-slate-700/50">
              <p className="text-sm font-medium text-slate-400 mb-3">Selecione a versão:</p>
              <div className="flex flex-wrap gap-2">
                {data.versoes.map((version, index) => (
                  <button
                    key={version.codigoFipe}
                    onClick={() => setSelectedVersionIndex(index)}
                    className={`px-4 py-2 rounded-xl text-sm transition-all ${
                      selectedVersionIndex === index
                        ? 'bg-green-600 text-white'
                        : 'bg-slate-800/50 text-slate-300 hover:bg-slate-700/50'
                    }`}
                  >
                    {version.modeloVersao.length > 40 
                      ? version.modeloVersao.substring(0, 40) + '...'
                      : version.modeloVersao
                    }
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Selected Version Details */}
          {selectedVersion && (
            <div className="p-6 space-y-6">
              {/* Price Summary */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {/* Current Price */}
                <div className="col-span-2 p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 rounded-2xl border border-green-500/30">
                  <div className="flex items-center gap-2 text-green-400 text-sm mb-2">
                    <DollarSign className="w-4 h-4" />
                    Preço FIPE Atual
                  </div>
                  <p className="text-4xl font-bold text-white mb-2">
                    {selectedVersion.precoAtualFormatado}
                  </p>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <span className="text-sm text-slate-400">
                      Referência: {selectedVersion.mesReferencia.replace('_', '/')}
                    </span>
                  </div>
                </div>

                {/* Variation */}
                <div className={`p-6 rounded-2xl border ${getTendenciaBg(selectedVersion.tendencia)}`}>
                  <div className="flex items-center gap-2 text-slate-400 text-sm mb-2">
                    <BarChart3 className="w-4 h-4" />
                    Variação 12 meses
                  </div>
                  <div className="flex items-center gap-2">
                    {getTendenciaIcon(selectedVersion.tendencia)}
                    <span className={`text-3xl font-bold ${getTendenciaColor(selectedVersion.tendencia)}`}>
                      {selectedVersion.variacao12Meses > 0 ? '+' : ''}
                      {selectedVersion.variacao12Meses.toFixed(1)}%
                    </span>
                  </div>
                  <p className="text-sm text-slate-500 mt-1">
                    {selectedVersion.tendencia === 'alta' ? 'Valorização' : 
                     selectedVersion.tendencia === 'baixa' ? 'Desvalorização' : 'Estável'}
                  </p>
                </div>
              </div>

              {/* FIPE Code */}
              <div className="flex items-center gap-4 p-4 bg-slate-800/50 rounded-xl">
                <div className="flex items-center gap-2 text-slate-400">
                  <Tag className="w-4 h-4" />
                  <span className="text-sm">Código FIPE:</span>
                </div>
                <span className="font-mono text-white font-medium">{selectedVersion.codigoFipe}</span>
                <button
                  onClick={() => copyToClipboard(selectedVersion.codigoFipe, 'fipe')}
                  className={`ml-auto p-2 rounded-lg transition-colors ${
                    copied === 'fipe'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-slate-700/50 text-slate-400 hover:text-white'
                  }`}
                >
                  {copied === 'fipe' ? <CheckCircle className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>

              {/* Price Chart */}
              {selectedVersion.historico.length > 0 && (
                <div className="p-6 bg-slate-800/30 rounded-2xl border border-slate-700/30">
                  <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-green-400" />
                    Evolução do Preço (12 meses)
                  </h3>
                  
                  {/* Simple SVG Chart */}
                  <div className="relative h-[180px] w-full">
                    <svg 
                      viewBox={`0 0 ${chartWidth} ${chartHeight + 40}`} 
                      className="w-full h-full"
                      preserveAspectRatio="none"
                    >
                      {/* Grid lines */}
                      {[0, 25, 50, 75, 100].map((y) => (
                        <line
                          key={y}
                          x1="0"
                          y1={chartHeight * (y / 100)}
                          x2={chartWidth}
                          y2={chartHeight * (y / 100)}
                          stroke="rgba(100, 116, 139, 0.2)"
                          strokeDasharray="2,2"
                        />
                      ))}
                      
                      {/* Area fill */}
                      <path
                        d={getChartArea(selectedVersion)}
                        fill="url(#gradient)"
                        opacity="0.3"
                      />
                      
                      {/* Line */}
                      <path
                        d={getChartPath(selectedVersion)}
                        fill="none"
                        stroke="#22c55e"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      
                      {/* Dots */}
                      {selectedVersion.historico.map((h, i) => {
                        const prices = selectedVersion.historico.map(p => p.preco);
                        const minPrice = Math.min(...prices) * 0.98;
                        const maxPrice = Math.max(...prices) * 1.02;
                        const priceRange = maxPrice - minPrice;
                        
                        const x = (i / (selectedVersion.historico.length - 1)) * chartWidth;
                        const y = chartHeight - ((h.preco - minPrice) / priceRange) * chartHeight;
                        
                        return (
                          <circle
                            key={h.mes}
                            cx={x}
                            cy={y}
                            r="3"
                            fill="#22c55e"
                            className="hover:r-5 transition-all"
                          >
                            <title>{`${h.mesFormatado}: ${h.precoFormatado}`}</title>
                          </circle>
                        );
                      })}
                      
                      {/* Gradient definition */}
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                          <stop offset="0%" stopColor="#22c55e" stopOpacity="0.5" />
                          <stop offset="100%" stopColor="#22c55e" stopOpacity="0" />
                        </linearGradient>
                      </defs>
                    </svg>
                    
                    {/* X-axis labels */}
                    <div className="absolute bottom-0 left-0 right-0 flex justify-between text-[10px] text-slate-500 px-1">
                      {selectedVersion.historico.filter((_, i) => i % 3 === 0 || i === selectedVersion.historico.length - 1).map((h) => (
                        <span key={h.mes}>{h.mesFormatado}</span>
                      ))}
                    </div>
                  </div>

                  {/* Price Range */}
                  <div className="flex justify-between mt-4 text-sm">
                    <div>
                      <p className="text-slate-500">Menor valor</p>
                      <p className="text-white font-medium">
                        {Math.min(...selectedVersion.historico.map(h => h.preco)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-slate-500">Maior valor</p>
                      <p className="text-white font-medium">
                        {Math.max(...selectedVersion.historico.map(h => h.preco)).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* History Table */}
              <div className="p-6 bg-slate-800/30 rounded-2xl border border-slate-700/30">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-green-400" />
                  Histórico de Preços
                </h3>
                
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="text-left text-sm text-slate-500 border-b border-slate-700/50">
                        <th className="pb-3 font-medium">Mês</th>
                        <th className="pb-3 font-medium text-right">Valor</th>
                        <th className="pb-3 font-medium text-right">Variação</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...selectedVersion.historico].reverse().map((h, index, arr) => {
                        const prevPrice = arr[index + 1]?.preco;
                        const variation = prevPrice ? ((h.preco - prevPrice) / prevPrice) * 100 : 0;
                        
                        return (
                          <tr key={h.mes} className="border-b border-slate-800/50 last:border-0">
                            <td className="py-3 text-slate-300">{h.mesFormatado}</td>
                            <td className="py-3 text-right font-mono text-white">{h.precoFormatado}</td>
                            <td className={`py-3 text-right font-mono ${
                              variation > 0 ? 'text-green-400' : variation < 0 ? 'text-red-400' : 'text-slate-500'
                            }`}>
                              {index < arr.length - 1 && (
                                <>
                                  {variation > 0 ? '+' : ''}
                                  {variation.toFixed(1)}%
                                </>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Version Info */}
              <div className="p-4 bg-slate-800/30 rounded-xl">
                <p className="text-sm text-slate-400">
                  <span className="font-medium text-white">Versão:</span> {selectedVersion.modeloVersao}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

export default ConsultaPrecoFipeResult;

